import CancelablePromise from '@atpoint/atpjs/cancelable_promise'
import i18n from '@atpoint/atpjs/helpers/i18n'
import StepLib from '@atpoint/atpjs/wizard/step'
import { makeProps } from '@atpoint/atpjs/wizard/view'
import { scrollToFirstError, scrollTop } from '@merchant/helpers/utils'
import mapping from '@merchant/mapping'
import { Map as immutableMap } from 'immutable'
import { createRef } from 'react'
import { BackendContext } from 'swisscard/helpers/backend_context'

import { applicationError, applicationSuccess, formStep } from '../../tracking/tracking_utils'

import type DataHandler from '@atpoint/atpjs/data_handler'
import type { PathType } from '@atpoint/atpjs/data_handler'
import type {
  ErrorMessageType,
  ValidationObjectType,
  ValidationResultType,
} from '@atpoint/atpjs/validation/validationsLib'
import type { StepPropsType as AtpjsStepPropsType } from '@atpoint/atpjs/wizard/step'
import type { ViewPropsType } from '@atpoint/atpjs/wizard/view'
import type Wizard from '@merchant/views/wizard'

export type StepPropsType = AtpjsStepPropsType & { trackingContext: typeof Wizard.prototype.trackingContext }

const transPre = i18n.prefix(['attributes', 'errors'])

const generateErrorPaths = (
  obj: ValidationResultType | ValidationResultType[] | ErrorMessageType,
  prefix: string[] = [],
  result: string[][] = [],
): string[][] => {
  if (Array.isArray(obj)) {
    obj.forEach((entry, index) => {
      generateErrorPaths(entry, prefix.concat(String(index)), result)
    })
  } else if (typeof obj === 'object' && obj !== null) {
    for (const key of Object.keys(obj)) {
      generateErrorPaths(obj[key], prefix.concat(key), result)
    }
  } else if (obj) {
    result.push(prefix)
  }
  return result
}

const filterJoinErrors = (errors: Array<Array<string>>) => errors.map((line) => line.join('.'))

class Step<Props extends StepPropsType> extends StepLib<Props> {
  formElement = createRef<HTMLFormElement>()
  sendData = true
  static mapping = mapping
  static contextType = BackendContext

  constructor(props: Props) {
    super(props, {})
  }

  componentDidMount(): void {
    const { trackingContext } = this.props

    if (trackingContext) {
      formStep(trackingContext(this.getHandler().get('id')))
    }
  }

  next(step?: string) {
    const { trackingContext } = this.props

    return super.next(step).then(
      ([newStep, serverData]) => {
        serverData = serverData || this.getHandler()
        const nextStep = newStep || serverData.get('step')
        serverData.getData().set('step', nextStep)

        const data = this.getHandler()
        const id = data.get('id')

        if (newStep === 'finish') {
          const key = `swisscard_merchant_tracking_applicationSuccess_${id}`
          const value = window.localStorage.getItem(key)

          if (value !== 'applicationSuccess') {
            applicationSuccess(trackingContext(data.get('id')))
            window.localStorage.setItem(key, 'applicationSuccess')
          }
        }

        scrollTop()
        return [newStep, serverData]
      },
      () => {
        const dataErrors = this.validate(true, 'data', this.validations(transPre))
        const modalErrors = this.validate(true, 'modal', this.modalValidations(transPre))

        const backendErrors = (this.getModal(['backendValidations']) || immutableMap()).keySeq().toArray()
        const errors = filterJoinErrors(generateErrorPaths(dataErrors))
          .concat(filterJoinErrors(generateErrorPaths(modalErrors)))
          .concat(backendErrors)
        applicationError({
          ...trackingContext(this.getHandler().get('id')),
          error_field: errors.join(','),
        })
        scrollToFirstError(this.formElement)
        return CancelablePromise.reject()
      },
    )
  }

  previous = (step?: string) => {
    const { data } = this.props

    return data.replace(this.getHandler()).then(() => {
      return super.previous(step)
    })
  }

  inputSetProps(
    state?: string,
    globalOptions?: Record<string, unknown> | null | undefined,
  ): (path: PathType, options?: Record<string, unknown>) => ViewPropsType & { data: DataHandler } {
    const stateOrDefault = state || 'data'
    const data = this.getHandler(stateOrDefault)
    const validations: ValidationObjectType =
      stateOrDefault === 'data' ? this.validations(transPre) : this.modalValidations(transPre)
    const output = this.validate(this.displayErrors, stateOrDefault, validations)

    return (...params) => {
      const propsGetter = makeProps(data, output, this.getModal(['backendValidations']), globalOptions)
      const props = propsGetter(...params)

      return {
        ...props,
        data,
      }
    }
  }
}

export default Step
